import { idSeed } from '@/core/rng';
import type { TargetKind } from './attention';
import type { LoreLine } from './lore';
import type { CostBandName, Verb } from './hud';

/**
 * ONE SELECTED THING = ONE CARD.
 *
 * Everything the god is told about whatever it has chosen arrives here, in one
 * place, on the left. Before this there were four surfaces: a hover line and a
 * chosen line in the top right, a followed life top left, and a place's history
 * bottom left — so understanding one tree meant reading three corners and
 * knowing which of them was talking about the tree.
 *
 * **Two cards were built at once**, on two branches, and this is both of them.
 * `v05-discovery` built the stat card — *what is this, and what does this god
 * understand about it?* — with the seal, law 32's standing ladder, and the
 * verb register that says what a cast would find here. This branch built the
 * compositional shell that folded the hover line, the life panel and the place
 * panel into one surface. Neither was the other's rough draft: the shell is
 * what makes the card survive the simulation getting richer, and the verb
 * register is the half that makes it worth opening. Both are here.
 *
 * The card is **compositional, not per-kind.** There is no TreeCard and no
 * PersonCard: a subject supplies whichever sections it can answer and the shell
 * omits the rest, so a pebble and a hundred-year-old settlement read in the
 * same language at different lengths. A new kind of thing the world can know is
 * a new phrase in `ui/subject.ts`, never a new panel.
 *
 * The order is fixed because it is the order the questions come in:
 *
 *     what is it?  →  what is happening?  →  what could I do about it?
 *                  →  what has happened?  →  why might I care?
 *
 * Three rules it inherits whole, and they are what keep it from becoming an
 * inspector:
 *
 * **A phrase, never a row.** `ui/lives.ts` and `ui/lore.ts` already turn state
 * into words; nothing here prints a quantity that has to be compared against
 * another quantity. *Healthy*, *old growth*, *a fire passed through here* —
 * never `vitality: 0.7284`.
 *
 * **A person is not a readout, structurally.** On a person every *reading* is
 * checked before it reaches the document and any line carrying a digit is
 * withheld. What is not checked is the identity: `kind` is one of a fixed set
 * of words and `name` is rendered as given, because a card that withheld a
 * person's own name would not be a card. `test/follow.ts` hands `cardHTML` a
 * deliberately numeric person and asserts not one digit survives.
 *
 * **`world` and `yours` are never interleaved.** The first is what anyone
 * standing here could come to know. The second is the god's own record of its
 * own hand, and law 10 is why they are two lists rather than one sorted one:
 * nothing in the world can say whose doing it was.
 *
 * Render-only. Building a view and painting it writes nothing anywhere, which
 * is law 9 — selection is attention, and attention is free.
 */

/**
 * Where on law 32's ladder this thing stands: **Ordinary → Promoted →
 * Consecrated → Mythic**, each crossed by accumulated consequence and none of
 * them by a menu. The card shows no meter and no progress — an ordinary thing
 * says nothing at all, which is what makes the first promoted thing a player
 * stumbles on actually land.
 */
export type CardStanding = 'ordinary' | 'promoted' | 'consecrated' | 'mythic';

/**
 * What one verb would find in this subject — the reason the card is worth
 * opening rather than merely reading.
 *
 * Two independent readings, and keeping them apart is the whole design:
 * `cost` is what it would take of **you**, and `purchase` is how much of the
 * process the verb acts on is happening **here** — `leverage.ts`'s
 * `processViability`, in words. A cheap cast into ground with nothing to work
 * with is the trap the number was always hiding, and a card that showed only
 * cost would keep hiding it.
 *
 * Neither is an outcome. Law 14: naming the thing says where causal pressure
 * lands, never what results, so nothing here may promise an effect.
 */
export interface CardVerb {
  verb: Verb;
  cost: CostBandName;
  purchase: 'none' | 'little' | 'some' | 'strong';
  /**
   * One clause in the land's own voice, when there is something true to add.
   * It goes through the same withholding as every other reading on the card:
   * a note carrying a digit is dropped rather than printed.
   */
  note?: string;
}

/** How a memory reached the mind that holds it, resolved to a phrase. */
export interface CardMemory {
  eventId: string;
  /** World-day, for the gutter — omitted on a person's card; see `cardHTML`. */
  day: number;
  /** "was there", "heard it" — absent where the memory is the community's. */
  how?: string;
}

export interface CardView {
  /**
   * The thing's own identity — `Target.id`, which for baseline world content
   * is its position (law 1). The seal is drawn from it, so the same tree bears
   * the same seal on every machine for ever, and a different one from its
   * neighbour.
   */
  id: string;
  kind: TargetKind;

  /* ---- identity: what is this? ---- */
  name: string;
  /** The second clause the world would add — `Target.note`. */
  note?: string;
  /**
   * Where it belongs: the country it stands in, and how far out from the
   * presence. One line, never a table.
   */
  of?: string;
  /**
   * WHAT THE SUBJECT IS AT THE SCALE THE PLAYER IS HOLDING — the card's half
   * of semantic scroll.
   *
   * The thread does not change when the wheel turns: the same person keeps the
   * card, keeps the name, keeps the seal. What changes is which truth about
   * them is currently in front of you, and this line is what makes that
   * legible rather than mysterious. Absent at the intimate register, because
   * *them* is what a card about a person already means and a line saying so
   * would be the panel narrating itself.
   */
  at?: string;
  standing: CardStanding;
  /**
   * Significance is signed (`world/things.ts`): a lightning-split oak is as
   * much a thing history kept as a blessed one. Set when what promoted this
   * was damage, so the card does not read the two alike.
   */
  wrong?: boolean;

  /* ---- immediate state: what is true right now? ---- */
  now: readonly string[];

  /* ---- what could I do about it? ---- */
  /**
   * Every verb's reading of this subject. The card renders AT MOST ONE of
   * these — the armed one — and see `armed` for why.
   */
  verbs: readonly CardVerb[];
  /**
   * The verb the god is actually holding, or undefined for none.
   *
   * ## Why the card stopped printing all five
   *
   * It used to render the whole register under *what a verb would find here*:
   * five rows, one per verb, on every card. Measured off its own line boxes
   * that block was ~173 px, close to a third of a full card and its single
   * largest section — and three of the five rows said "strong here" on every
   * card in the game, because omen, strike and rain are not scaled by
   * `processViability` at all and `cardFor` hardcoded them. A third of the
   * space a subject had to describe itself went to a table that was constant
   * across subjects, which is the definition of a system inspector.
   *
   * A god holding Strengthen is not asking what Strike would cost. So the card
   * answers the question actually being asked, and says nothing when none is.
   */
  armed?: string;
  /**
   * What the armed verb would take hold of on THIS subject, strongest first —
   * see `HudPerson.takes`. Rendered only under Strengthen, because it is the
   * only verb whose purchase is a property of the subject's own expression.
   *
   * A person with none is not hidden: the card says so, because "there was
   * nothing in them to lean on today" is a fact the player needs, and is the
   * difference between that and "the god did nothing".
   */
  takes?: readonly string[];
  /**
   * Why the armed verb is priced as it is — `costReasons`, already worded.
   * Four fixed phrases today ("a wide attention", "far from you", "against the
   * land", "with the land"), none numeric.
   */
  why?: readonly string[];

  /* ---- context: what a mind carries, where the subject has one ---- */
  carries?: {
    heading: string;
    lines: readonly CardMemory[];
  };

  /* ---- history: what has happened to it? ---- */
  world: readonly LoreLine[];
  /**
   * Set when the reading is a DIFF — the world-year the god last put a hand
   * here. The heading changes and the list is only what happened since.
   */
  sinceYear?: number;
  /** The god's own register, kept apart from the world's (law 10). */
  yours: readonly LoreLine[];

  /* ---- relationship: why might this god care? ---- */
  significance: readonly string[];

  /**
   * Set when the subject is no longer there to watch — a life ended, a herd
   * walked out of sight, a thing carried off. The card says so and the camera
   * lets go; nothing is silently substituted.
   */
  lost?: string;
}

export interface Card {
  /** Paint it, or take it off screen when there is nothing chosen. */
  update(v: CardView | null): void;
}

/** What the Chronicle made of an event id, when it made anything. */
export type ResolveMemory = (eventId: string) => { text: string; grim: boolean } | null;

/** Text into a document. Hoisted, because `cardHTML` is a pure export. */
const escapeText = (t: string): string =>
  t.replace(/[<&]/g, (c) => (c === '<' ? '&lt;' : '&amp;'));

/**
 * The seal — a card's own mark, drawn from the thing's own id.
 *
 * A god meets ten thousand oaks. What lets one of them become *the* oak is
 * being recognisable, and a card that reads `a spreading oak` over and over is
 * the opposite of that. So the frame carries a small device built out of
 * `idSeed(id)`: how many spokes, how many sides to the inner figure, its
 * rotation, and how the outer ring is broken. Baseline world content is
 * identified by its position (law 1), so **the same tree bears the same seal
 * on every machine, for ever** — no store, no bookkeeping, nothing minted.
 *
 * Measured, `tools/chrome.ts`: the five fields give 18,432 distinguishable
 * seals, and over 161,600 real ids (a 400×400 lattice of `placeKey`s plus
 * 1,600 person ids) 18,425 of them were actually hit. The number that matters
 * is not global uniqueness — that is impossible and is not the point — but
 * whether two things a player could meet in one place collide: over 300
 * neighbourhoods of 24 adjacent things, the worst had **one** collision.
 */
export function seal(id: string, standing: CardStanding, wrong = false): string {
  const h = idSeed(id) >>> 0;
  // the exact five fields tools/chrome.ts counted — change one and the
  // distinctness measurement above stops describing what ships
  const spokes = 5 + (h % 8);
  const sides = 3 + ((h >>> 3) % 6);
  const phase = (((h >>> 7) % 12) / 12) * Math.PI * 2;
  const tilt = (((h >>> 11) % 8) / 8) * Math.PI * 2;
  const gaps = 1 + ((h >>> 15) % 4);

  const C = 20, R = 17.2;
  const at = (r: number, a: number) =>
    `${(C + r * Math.cos(a)).toFixed(2)} ${(C + r * Math.sin(a)).toFixed(2)}`;

  // the outer ring, broken into `gaps` arcs — a ring drawn by hand does not
  // close, and an unclosed ring is the difference between a device and a dot
  const arc = 2 * Math.PI / gaps;
  const ring = Array.from({ length: gaps }, (_, i) => {
    const a0 = phase + i * arc + 0.16, a1 = phase + (i + 1) * arc - 0.16;
    return `M ${at(R, a0)} A ${R} ${R} 0 ${a1 - a0 > Math.PI ? 1 : 0} 1 ${at(R, a1)}`;
  }).join(' ');

  const ticks = Array.from({ length: spokes }, (_, i) => {
    const a = phase + (i * 2 * Math.PI) / spokes;
    return `M ${at(10.4, a)} L ${at(13.6, a)}`;
  }).join(' ');

  const poly = Array.from({ length: sides }, (_, i) =>
    at(7.4, tilt + (i * 2 * Math.PI) / sides)).join(' L ');

  // mythic things are drawn with the ink rather than the ornament gold: the
  // seal is the first thing that says a thing has left the ordinary, and it
  // has to say it before any word does
  const outer = wrong ? 'var(--grim)' : 'var(--gold-deep)';
  const inner = wrong ? 'var(--grim)'
    : standing === 'mythic' ? 'var(--ink)' : 'var(--gold)';
  const heavy = standing === 'mythic' || standing === 'consecrated';

  return `<svg viewBox="0 0 40 40" aria-hidden="true">` +
    `<path d="${ring}" fill="none" stroke="${outer}" stroke-width="${heavy ? 1.1 : 0.8}"` +
    ` stroke-linecap="round" opacity="${heavy ? 0.95 : 0.7}"/>` +
    `<path d="${ticks}" fill="none" stroke="${outer}" stroke-width="0.7"` +
    ` stroke-linecap="round" opacity="0.55"/>` +
    `<path d="M ${poly} Z" fill="none" stroke="${inner}" stroke-width="0.9"` +
    ` stroke-linejoin="round" opacity="${heavy ? 0.95 : 0.75}"/>` +
    (standing === 'mythic'
      ? `<circle cx="20" cy="20" r="2.1" fill="${inner}" opacity="0.85"/>`
      : `<circle cx="20" cy="20" r="1" fill="${inner}" opacity="0.6"/>`) +
    `</svg>`;
}

const VERB_GLYPH: Record<Verb, string> = {
  strike: '⚡', rain: '☂', strengthen: '❁', weaken: '☾', omen: '✷', grasp: '✋',
};

/** `processViability`, said the way a person would say it. */
const PURCHASE: Record<CardVerb['purchase'], string> = {
  none: 'nothing here',
  little: 'little here',
  some: 'some here',
  strong: 'strong here',
};

/**
 * A person is not a readout.
 *
 * `namePerson` keeps this rule for the hover line and `lives.ts` keeps it for
 * the life's own phrases. The card is the third place it could break and by
 * far the most tempting, because a card *wants* numbers. So on a person the
 * rule is structural rather than editorial: any line that would carry a digit
 * is **withheld**, never invented around and never quietly mangled into
 * something that still looks like a reading.
 */
const carriesNumber = (t: string | undefined): boolean => !!t && /\d/.test(t);

/**
 * Vite defines `import.meta.env` at build time; `tsx` running a suite under
 * node does not, hence the optional chain.
 */
const DEV = import.meta.env?.DEV === true;

const YEARS = (ls: readonly LoreLine[], dated: boolean, hide: (t?: string) => string | undefined) =>
  ls.map((l) => {
    const text = hide(l.text);
    if (!text) return '';
    return `<li class="${l.tone}">` +
      (dated ? `<span class="d">y${l.year}</span>` : '') +
      `<span>${escapeText(text)}</span></li>`;
  }).join('');

/**
 * The card, as a string of HTML — pure, so the tripwire can exercise the code
 * that actually paints rather than a second copy of its rules.
 *
 * `held` is whether the subject is the god's CHOSEN thing rather than merely
 * the one under the pointer, and the only thing it changes is the footer.
 */
export function cardHTML(v: CardView, held = true, resolve?: ResolveMemory): string {
  const person = v.kind === 'person';
  const hide = (t: string | undefined): string | undefined => {
    if (!person || !carriesNumber(t)) return t;
    if (DEV) {
      console.error(`card: a number about a person was withheld: ${t}`);
    }
    return undefined;
  };
  const keep = (ss: readonly string[]): string[] =>
    ss.map((s) => hide(s)).filter((s): s is string => !!s);

  const note = hide(v.note), of = hide(v.of), lost = hide(v.lost);

  const standing = v.standing === 'ordinary' && !v.wrong ? '' :
    `<div class="standing ${v.standing}${v.wrong ? ' wrong' : ''}">` +
    `${v.wrong && v.standing === 'ordinary' ? 'marked' : v.standing}</div>`;

  const now = keep(v.now);
  const nowRows = now.length
    ? `<div class="now">${now.map((s) => `<div>${escapeText(s)}</div>`).join('')}</div>` : '';

  const sig = keep(v.significance);
  const sigRows = sig.length
    ? `<div class="sig">${sig.map((s) => `<div>${escapeText(s)}</div>`).join('')}</div>` : '';

  // ONE verb, and only the one being held. See `CardView.armed`.
  const why = keep(v.why ?? []);
  const held_ = v.armed ? v.verbs.find((cv) => cv.verb === v.armed) : undefined;
  const castRows = held_
    ? `<ul class="cast">${(() => {
        const vnote = hide(held_.note);
        return `<li class="${held_.purchase === 'none' ? 'none ' : held_.purchase === 'strong' ? 'strong ' : ''}${held_.cost}">` +
          `<span class="g">${VERB_GLYPH[held_.verb] ?? '·'}</span>` +
          `<span class="n">${held_.verb}</span>` +
          `<span class="p">${PURCHASE[held_.purchase]}</span>` +
          `<span class="band">${held_.cost}</span>` +
          (vnote ? `<span class="vnote">${escapeText(vnote)}</span>` : '') +
          `</li>`;
      })()}</ul>` +
      (why.length ? `<div class="why">${escapeText(why.join(' · '))}</div>` : '')
    : '';

  // What Strengthen would find in them. Only under Strengthen, and only where
  // the subject has an expression to reinforce at all.
  const takes = v.armed === 'strengthen' && v.takes
    ? (v.takes.length
        ? `<div class="takes">${v.takes.map((t) => `<span>${escapeText(t)}</span>`).join('')}</div>`
        : `<div class="takes none">nothing in them to take hold of today</div>`)
    : '';

  let carries = '';
  if (v.carries && resolve) {
    // The same thing remembered three times is one line, said once and folded
    // — `ui/lore.ts`'s *and more than once*, not three identical rows. A head
    // that carries three hard seasons does carry three; a card that prints
    // *hard times at the hearth* three times over reads as a stutter, and the
    // fold is what lets a fourth, different memory reach the four lines shown.
    const folded: { m: CardMemory; said: { text: string; grim: boolean }; n: number }[] = [];
    for (const m of v.carries.lines) {
      const said = resolve(m.eventId);
      if (!said) continue;
      const same = folded.find((f) => f.said.text === said.text);
      if (same) same.n++;
      else folded.push({ m, said, n: 1 });
    }
    const said = folded
      .slice(0, 4)
      .map((o) => {
        const once = hide(o.said.text);
        if (!once) return '';
        const text = o.n === 1 ? once : o.n === 2 ? `${once}, and again` : `${once}, and more than once`;
        // **A life is dated by what happened, not by when.** Every other card
        // prints a `d{day}` or `y{year}` gutter and a person's does not: the
        // guard is structural and a date is a digit, and rather than punching
        // a hole in the wall for one field the date simply goes. What the
        // person carries is the event, not the calendar.
        return `<li${o.said.grim ? ' class="grim"' : ''}>` +
          (person ? '' : `<span class="d">d${o.m.day}</span>`) +
          `<span>${escapeText(text)}` +
          `${o.m.how ? `<em> · ${escapeText(o.m.how)}</em>` : ''}</span></li>`;
      }).join('');
    carries = `<h3>${escapeText(v.carries.heading)}</h3>` + (said
      ? `<ul class="mem">${said}</ul>`
      : `<div class="none">nothing yet worth keeping</div>`);
  }

  // Silence is the honest answer over most ground — measured at 94.3% of
  // random land — and saying so plainly is what makes a place that DOES have a
  // history feel unlike the hillside beside it.
  const heading = v.sinceYear !== undefined
    ? 'since you were last here' : 'what it has lived through';
  // The place's history is not a number about a person. A mark's *since year
  // 26* is a fact about the ground under their feet — the same line a tree's
  // card shows — and the wall was dropping it from every person's card and
  // logging the loss each frame. The year gutter stays off for a person
  // (a life is dated by what happened, not by when); the text passes.
  const worldRows = YEARS(v.world, !person, (t) => t);
  const world = `<h3>${heading}</h3>` + (worldRows
    ? `<ul>${worldRows}</ul>`
    : `<div class="none">nothing here that the land kept</div>`);

  const yoursRows = YEARS(v.yours, !person, (t) => t);
  const yours = yoursRows ? `<h3>your own hand</h3><ul>${yoursRows}</ul>` : '';

  return `<div class="body">` +
    `<div class="head">` +
      `<span class="seal">${seal(v.id, v.standing, v.wrong)}</span>` +
      `<div class="who">` +
        `<div class="kind">${escapeText(v.kind)}</div>` +
        `<h2>${escapeText(v.name)}</h2>` +
        standing +
      `</div>` +
    `</div>` +
    // the note spans the whole card rather than sitting beside the seal:
    // measured, the longest note the world can produce is 222 px of 10px mono
    // and the column beside a 34 px seal is only 187 px wide
    (note ? `<div class="epi">${escapeText(note)}</div>` : '') +
    (of ? `<div class="of">${escapeText(of)}</div>` : '') +
    (v.at ? `<div class="at">${escapeText(v.at)}</div>` : '') +
    (lost ? `<div class="lost">${escapeText(lost)}</div>` : '') +
    nowRows +
    sigRows +
    (castRows ? `<div class="rule"></div>${castRows}${takes}` : '') +
    carries +
    world +
    yours +
  `</div><footer>${held ? 'esc · let go' : 'look away'}</footer>`;
}

/**
 * Build the card over `#subject`.
 *
 * Rebuilt only when the HTML actually changes. The card is *read*, not
 * scanned: rewriting it sixty times a second would fight text selection, cost
 * a layout for nothing, and — because a reading is assembled from six stores —
 * do it for an answer that cannot change inside a world-day.
 */
export function createCard(resolve: ResolveMemory): Card {
  const el = document.getElementById('subject')!;
  let last = '';

  const update = (v: CardView | null) => {
    if (!v) {
      if (last !== '') { last = ''; el.classList.remove('show'); }
      return;
    }
    const html = cardHTML(v, true, resolve);
    if (html === last) return;
    last = html;
    el.innerHTML = html;
    el.classList.add('show');
    // The body CLIPS rather than scrolls — it sits inside a
    // `pointer-events:none` panel, and a scroll container the pointer can
    // never reach is worse than an honest cut. So what does not fit is FADED,
    // and this is the read that says whether it did. A read of the document,
    // which writes nothing anywhere (law 9).
    const body = el.querySelector<HTMLElement>('.body');
    el.classList.toggle('cut', !!body && body.scrollHeight > body.clientHeight + 1);
  };

  return { update };
}
